import { BookOpen, ExternalLink, FileText, Folder, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { FindingRow } from './findings-view';
import { useRuleTitle } from './rule-titles';

/**
 * One finding says three things (decision 189): what differs, which rule applies (a link to the
 * rule's anchor on the Regole page) and what to do. The patient-data kind is shown with care: its
 * own label and icon, calm words, and only the file's name.
 */
export function FindingCard({ row }: { readonly row: FindingRow }) {
  const { t } = useTranslation('archive');
  const { item, finding } = row;
  const patient = finding.kind === 'patient-data-suspected';
  const Icon = item.kind === 'folder' ? Folder : FileText;
  const ruleTitle = useRuleTitle(finding.ruleSection);
  return (
    <li className="border-border flex flex-col gap-3 border-t py-4 first:border-t-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2 font-semibold">
          <Icon aria-hidden="true" className="size-4 shrink-0" />
          <span className="min-w-0 break-words">{item.name}</span>
          {item.webViewLink ? (
            <a
              href={item.webViewLink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('common.openInDrive')}
              className="inline-flex min-h-11 min-w-11 items-center justify-center"
            >
              <ExternalLink aria-hidden="true" className="size-4" />
            </a>
          ) : null}
        </span>
        <span className="bg-muted inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold">
          {patient ? <ShieldAlert aria-hidden="true" className="size-3.5" /> : null}
          {t(`overview.kinds.${finding.kind}`, { defaultValue: finding.kind })}
        </span>
      </div>
      <div className="grid gap-3 text-sm sm:grid-cols-2">
        <div>
          <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
            {t('overview.differs')}
          </p>
          <p>{finding.message}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
            {t('overview.whatToDo')}
          </p>
          <p>{finding.fix}</p>
        </div>
      </div>
      {ruleTitle === undefined ? null : (
        <Link
          to={`/archive/rules#${finding.ruleSection}`}
          className="inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold underline"
        >
          <BookOpen aria-hidden="true" className="size-4 shrink-0" />
          {t('overview.ruleLink', { title: ruleTitle })}
        </Link>
      )}
      {patient ? (
        <p className="text-muted-foreground text-xs">{t('overview.patient.note')}</p>
      ) : null}
    </li>
  );
}
